import { Box } from '@mui/material';
import React, { useEffect } from 'react';
import ReactFlow, {
  Background,
  ConnectionMode,
  Controls,
  MiniMap,
  useReactFlow,
} from 'reactflow';
import ProjectPackage from '../../ProjectPackage';
import CustomConnectionLine from './CustomConnectionLine';
import CustomEdge from './CustomEdge';

/** Deploy status at a glance — the minimap dot is the ledger, not the render. */
const miniNodeColor = (node) => {
  if (node?.data?.isTemp) return 'transparent';
  switch (node?.data?.deploy_status) {
    case 'DEPLOYED':
      return '#43a047';
    case 'DEPLOYING':
    case 'PENDING':
      return '#fdd835';
    case 'FAILED':
      return '#e53935';
    default:
      return '#5c6470';
  }
};

function FitViewOnGraph({ graphKey }) {
  const { fitView } = useReactFlow();
  useEffect(() => {
    const id = requestAnimationFrame(() => {
      fitView({ padding: 0.22, maxZoom: 0.95 });
    });
    return () => cancelAnimationFrame(id);
  }, [fitView, graphKey]);
  return null;
}

const nodeTypes = {
  // Pass through React Flow's isConnectable (default true) so port handles work.
  custom: ProjectPackage,
};

const edgeTypes = {
  custom: CustomEdge,
};

const FlowCanvas = ({
  nodes,
  edges,
  onNodesChange,
  onEdgesChange,
  onEdgesDelete,
  onConnect,
  onConnectStart,
  onConnectEnd,
  onConnectCheck,
  onInit,
  onDrop,
  onDragOver,
  reactFlowWrapper,
}) => {
  const graphKey = nodes
    .map((n) => `${n.id}:${Math.round(n.position.x)}:${Math.round(n.position.y)}`)
    .join('|');

  const styledNodes = nodes.map((node) => ({
    ...node,
    style: node.data.isTemp
      ? {
          opacity: 0.5,
          pointerEvents: 'none',
        }
      : {},
  }));

  return (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        width: '100%',
        minHeight: 0,
        position: 'relative',
      }}
    >
      <div style={{ flexGrow: 1, height: '100%', minHeight: 0 }} ref={reactFlowWrapper}>
        <ReactFlow
          nodes={styledNodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onEdgesDelete={onEdgesDelete}
          deleteKeyCode={['Backspace', 'Delete']}
          edgesFocusable
          edgesUpdatable={false}
          onConnect={onConnect}
          onConnectStart={onConnectStart}
          onConnectEnd={onConnectEnd}
          isValidConnection={onConnectCheck}
          onInit={onInit}
          onDrop={onDrop}
          onDragOver={onDragOver}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          connectionLineComponent={CustomConnectionLine}
          connectionMode={ConnectionMode.Strict}
          fitView
          fitViewOptions={{ padding: 0.2, maxZoom: 0.95 }}
          minZoom={0.25}
          maxZoom={1.25}
        >
          <FitViewOnGraph graphKey={graphKey} />
          <Controls />
          <MiniMap
            pannable={false}
            zoomable={false}
            nodeColor={miniNodeColor}
            nodeStrokeWidth={2}
            style={{ width: 168, height: 116 }}
          />
          <Background variant="dots" gap={12} size={1} />
        </ReactFlow>
      </div>
    </Box>
  );
};

export default FlowCanvas;
